import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Pagination } from "shared/ui/pagination";
import { useUserStore } from "entities/user";
import { USER_ROLES, LK_EVENTS_ROUTE } from "shared/config/routes";
import { getAllEventsRequest } from "pages/lk/api/getAllEventsRequest";
import { formatDateForEvent } from "shared/lib/hooks/useDateFormatting";
import { useMobile } from "shared/lib/hooks";
import styles from "./ManagerEvents.module.scss";

const PAGE_SIZE = 15;

export const ManagerEvents = () => {
  const { role } = useUserStore();
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [events, setEvents] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: PAGE_SIZE,
    total: 0,
    pages: 1,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const isTablet = useMobile("950px");
  const isMobile = useMobile("830px");

  const loadEvents = async (currentPage, signal) => {
    try {
      setLoading(true);
      setError(null);

      const data = await getAllEventsRequest(
        { page: currentPage, pageSize: PAGE_SIZE },
        { signal },
      );

      if (signal.aborted) return;

      setEvents(Array.isArray(data?.total) ? data.total : []);
      setPagination(
        data?.pagination ?? {
          page: currentPage,
          pageSize: PAGE_SIZE,
          total: 0,
          pages: 1,
        },
      );
    } catch (e) {
      if (e.name !== "AbortError" && !signal.aborted) {
        setError(e);
      }
    } finally {
      if (!signal.aborted) {
        setLoading(false);
      }
    }
  };

  const handlePageChange = (nextPage) => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    setPage(nextPage);
  };

  const handleEventClick = (event) => {
    navigate(`${LK_EVENTS_ROUTE}/${event.id}`, {
      state: {
        eventId: event.id,
        eventHeader: event.header,
        eventDate: event.plannedDate,
      },
    });
  };

  useEffect(() => {
    if (role !== USER_ROLES.MANAGER) return;

    const controller = new AbortController();

    loadEvents(page, controller.signal);

    return () => {
      controller.abort();
    };
  }, [role, page]);

  if (role !== USER_ROLES.MANAGER) return null;

  return (
    <div className={styles.contentWrapper}>
      {loading && (
        <div className={styles.contentWrapper__stateBlock}>
          <p className={styles.contentWrapper__stateText}>Загрузка...</p>
        </div>
      )}
      {error && (
        <div className={styles.contentWrapper__stateBlock}>
          <p className={styles.contentWrapper__stateText}>Ошибка загрузки</p>
        </div>
      )}
      <div className={styles.contentWrapper__eventsBlock}>
        {!loading && !error && events.length === 0 && (
          <div className={styles.contentWrapper__stateBlock}>
            <p className={styles.contentWrapper__stateText}>
              У вас пока что нет мероприятий
            </p>
          </div>
        )}

        {!loading && !error && events.length !== 0 && (
          <div className={styles.contentWrapper__tableScroll}>
            <table className={styles.contentWrapper__table}>
              <colgroup>
                <col style={{ width: "60%" }} />
                {isMobile ? (
                  <col style={{ width: "40%" }} />
                ) : (
                  <>
                    <col style={{ width: "20%" }} />
                    <col style={{ width: "20%" }} />
                  </>
                )}
              </colgroup>
              <thead className={styles.contentWrapper__thead}>
                <tr>
                  <th>Название мероприятия</th>
                  {isMobile ? (
                    <th>Количество записей / оплат</th>
                  ) : (
                    <>
                      <th>Количество записей</th>
                      <th>Количество оплат</th>
                    </>
                  )}
                </tr>
              </thead>

              <tbody className={styles.contentWrapper__tbody}>
                {events.map((event) => (
                  <tr key={event.id} onClick={() => handleEventClick(event)}>
                    <td>
                      <div className={styles.contentWrapper__infoCell}>
                        {event.image && !isTablet && (
                          <img
                            src={`data:image/png;base64,${event.image}`}
                            alt={event.header}
                            className={styles.contentWrapper__img}
                          />
                        )}
                        <div className={styles.contentWrapper__text}>
                          <div className={styles.contentWrapper__title}>
                            {event.header}
                          </div>
                          <div className={styles.contentWrapper__date}>
                            {formatDateForEvent(event?.plannedDate)}
                          </div>
                        </div>
                      </div>
                    </td>
                    {isMobile ? (
                      <th className={styles.contentWrapper__countCell}>
                        {event.countRecord} / {event.countPayment}
                      </th>
                    ) : (
                      <>
                        <td className={styles.contentWrapper__countCell}>
                          {event.countRecord}
                        </td>
                        <td className={styles.contentWrapper__countCell}>
                          {event.countPayment}
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <div className={styles.contentWrapper__paginationBlock}>
        <Pagination
          page={pagination.page}
          totalPages={pagination.pages}
          onPageChange={handlePageChange}
        />
      </div>
    </div>
  );
};
